import { ICommonSettings, IFrontendUrlSetting } from "@kuika/kuika-cl-model";
import { Form, FormInstance } from "antd/es";
import { Guid } from "guid-typescript";
import * as React from "react";
import { CloudDomainService, IDomainDto } from "../../../../../services/cloud-domain-service";
import { IWithSDProps } from "../../../../../shared/hoc/screen-design-consumer";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../../shared/utility/save-status";
import { DefaultModal } from "../../../../common/default-modal";
import { type KuikaButtonStatus } from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import { ICommonConfigDeleteFunctionParams, ICommonConfigSaveFunctionParams } from "../app-settings-config-container";
import { ConfigModalBody, ConfigModalFooter, ConfigModalNote } from "../config-modal-parts";
import { ConfigField } from "../config-pane-parts";

export interface IConfigFrontendUrlSettingsState {
  /** Where the commit stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  /** Where Delete stands, on its own button. */
  deleteStatus: KuikaButtonStatus;
  editingConfig?: IFrontendUrlSetting;
  domains: IDomainDto[];
  loadingDomains: boolean;
  domainWarning?: string;
}

export interface IConfigFrontendUrlSettingsProps {
  formInputName: string[];
  closeModal: any;
  showModal: boolean;
  editingFrontendUrlId?: string;
  commonSettings: ICommonSettings;
  isKuikaCloud?: boolean;
  getSectionDescription: (contentKey: string) => React.ReactNode;
  handleCreateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleUpdateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleDeleteCommonConfig: (params: ICommonConfigDeleteFunctionParams) => void;
}

export class ConfigFrontendUrlSettings extends React.PureComponent<
  IConfigFrontendUrlSettingsProps,
  IConfigFrontendUrlSettingsState
> {
  formRef = React.createRef<FormInstance>();
  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  /** Delete has its own machine: the control that was pressed is the one that reports. */
  private readonly deleteStatus: SaveStatus = new SaveStatus((deleteStatus: KuikaButtonStatus): void => {
    this.setState({ deleteStatus });
  });

  constructor(props: IConfigFrontendUrlSettingsProps) {
    super(props);
    this.state = {
      saveStatus: "idle",
      deleteStatus: "idle",
      domains: [],
      loadingDomains: false
    };
  }

  componentDidMount() {
    this.loadSetting();
    if (this.props.isKuikaCloud && this.props.showModal) {
      this.loadDomains();
    }
  }

  componentWillUnmount() {
    this.saveStatus.dispose();
    this.deleteStatus.dispose();
  }

  componentDidUpdate(prevProps: IConfigFrontendUrlSettingsProps & IWithSDProps) {
    if (prevProps.editingFrontendUrlId !== this.props.editingFrontendUrlId) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
    } else if (this.props.showModal !== prevProps.showModal) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
      if (this.props.isKuikaCloud && this.props.showModal) {
        this.loadDomains();
      }
    }
  }

  loadDomains = async () => {
    this.setState({ loadingDomains: true });
    try {
      const response = await CloudDomainService.GetDomains();
      if (response.data) {
        this.setState({ domains: response.data });
      }
    } catch (error) {
      console.error("Failed to load domains:", error);
    } finally {
      this.setState({ loadingDomains: false });
    }
  };

  extractBaseDomain = (url: string): string => {
    try {
      // Remove protocol if present
      let cleanUrl = url.replace(/^https?:\/\//, "").replace(/^www\./, "");
      // Remove path if present
      cleanUrl = cleanUrl.split("/")[0];
      // Get parts
      const parts = cleanUrl.split(".");
      // Return last two parts (e.g., "ahmet.com" from "a.ahmet.com")
      if (parts.length >= 2) {
        return parts.slice(-2).join(".");
      }
      return cleanUrl;
    } catch {
      return url;
    }
  };

  domainValidator = async (_rule, value) => {
    if (!this.props.isKuikaCloud || !value) {
      this.setState({ domainWarning: undefined });
      return;
    }

    const baseDomain = this.extractBaseDomain(value);
    const matchedDomain = this.state.domains.find(
      (d) => d.domain === baseDomain || d.baseDomain === baseDomain || d.domain === `*.${baseDomain}`
    );

    if (!matchedDomain) {
      this.setState({ domainWarning: undefined });
      throw new Error(
        `Domain "${baseDomain}" not found. Please add it to Cloud Domains first (Management > Cloud > Domains)`
      );
    }

    // Check if domain is in pending or provisioning state
    const status = matchedDomain.status?.toLowerCase();
    if (status === "pending" || status === "provisioning") {
      this.setState({
        domainWarning: `Warning: Domain "${baseDomain}" is currently in ${status} state. SSL certificate may not be active yet.`
      });
    } else {
      this.setState({ domainWarning: undefined });
    }
  };

  loadSetting = () => {
    if (!this.props.editingFrontendUrlId) return;

    const editingConfig = this.props.commonSettings.frontendUrlSettings?.find(
      (x) => x.id === this.props.editingFrontendUrlId
    );

    if (!editingConfig) return;
    this.setState({ editingConfig }, () => {
      this.formRef.current?.resetFields();
      this.formRef.current?.setFieldsValue(editingConfig);
    });
  };

  existingNameValidator = async (_rule, value) => {
    const frontendUrlSetting = this.props.commonSettings?.frontendUrlSettings;
    if (!frontendUrlSetting) return;

    const isSettingAlreadyExist =
      frontendUrlSetting.some((x) => x.name === value && x.id !== this.props.editingFrontendUrlId) ||
      value === "Default";

    if (isSettingAlreadyExist) throw new Error("Frontend URL Name. is already exist.");
  };

  onFinish = (data: any) => {
    this.saveStatus.start();
    let obj: IFrontendUrlSetting;
    obj = {
      id: Guid.create().toString(),
      name: data.name,
      virtualDirectory: data.virtualDirectory?.trim()
    };
    if (this.props.editingFrontendUrlId) {
      obj.id = this.props.editingFrontendUrlId;
      this.props.handleUpdateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "frontendUrlSettings",
        onSuccess: () => {
          // The ✓ goes up first and the dialog leaves after it — see `SaveStatus`.
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    } else {
      this.props.handleCreateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "frontendUrlSettings",
        onSuccess: () => {
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    }
  };

  onFinishFailed = (errorInfo: any) => {};

  deleteSetting = () => {
    if (!this.props.editingFrontendUrlId) {
      this.props.closeModal();
      return;
    }

    if (!this.deleteStatus.canStart) return;
    this.deleteStatus.start();
    this.props.handleDeleteCommonConfig({
      formInputName: this.props.formInputName,
      configId: this.props.editingFrontendUrlId || "",
      configKey: "frontendUrlSettings",

      onSuccess: () => {
        // Deleting says its outcome in a toast: the setting is gone and so is this dialog,
        // so there is nothing left on screen for a ✓ to sit on.
        this.deleteStatus.reset();
        this.props.closeModal();
        KMainFunctions.displaySuccessNotification(`Frontend URL setting has been deleted`);
      },
      onError: () => {
        this.deleteStatus.fail();
      },
      onFinal: () => {
        // A delete the container refuses — the setting is still in use — ends without an
        // outcome of its own, and the button would sit on "Deleting" for good.
        if (this.deleteStatus.current === "loading") this.deleteStatus.reset();
      }
    });
  };

  render() {
    return (
      <DefaultModal
        className="config-manager__modal"
        showModal={this.props.showModal}
        closeModal={this.props.closeModal}
        firstHeader="Frontend URL setting"
        secondHeader="Frontend URL setting"
        width={600}
        centered={true}
        bodyStyle={{ padding: 0 }}
        footer={
          <ConfigModalFooter
            onDelete={this.props.editingFrontendUrlId ? this.deleteSetting : undefined}
            deleteTestId="frontendUrlSettingDeleteButton"
            submitTestId={`frontendUrlSetting${this.props.editingFrontendUrlId ? "Update" : "Create"}Button`}
            submitLabel={this.props.editingFrontendUrlId ? "Update" : "Create"}
            status={this.state.saveStatus}
            deleteStatus={this.state.deleteStatus}
            submitForm="configFrontendUrlForm"
          />
        }
      >
        <Form
          id="configFrontendUrlForm"
          name="frontendUrlSetting"
          ref={this.formRef}
          onFinish={this.onFinish}
          onFinishFailed={this.onFinishFailed}
          style={{ height: "100%" }}
        >
          <ConfigModalBody description={this.props.getSectionDescription("web_frontendurlsetting_description")}>
            <ConfigField label="Name" required tooltipContentKey="web_frontendurlsetting_name">
              <Form.Item
                name="name"
                rules={[
                  {
                    required: true,
                    message: "Please enter a Frontend URL Name."
                  },
                  {
                    pattern: /^[A-Za-z][A-Za-z0-9 ]+$/,
                    message: "Frontend URL Name is invalid"
                  },
                  { validator: this.existingNameValidator }
                ]}
              >
                <KuikaInput size="md" data-testid="frontendUrlSettingNameInput" autoFocus />
              </Form.Item>
            </ConfigField>
            <ConfigField label="Frontend Url" required tooltipContentKey="web_frontendurlsetting_frontendurl">
              <Form.Item
                name="virtualDirectory"
                style={{
                  width: 380,
                  marginBottom: this.props.isKuikaCloud ? 0 : undefined
                }}
                rules={[
                  {
                    required: true,
                    message: "Please enter a Frontend Url."
                  },
                  { validator: this.domainValidator }
                ]}
              >
                <KuikaInput size="md" data-testid="webConfigFrontendUrlInput" />
              </Form.Item>
            </ConfigField>
            {this.props.isKuikaCloud && (
              <>
                {this.state.domainWarning && (
                  <ConfigModalNote tone="warning">{this.state.domainWarning}</ConfigModalNote>
                )}
                <ConfigModalNote tone="info">
                  <strong>DNS Configuration Required</strong>
                  <br />
                  Point your subdomain to static IP: <strong>34.49.36.235</strong>
                  <br />
                  Make sure the base domain exists in{" "}
                  <a href={`/${(window as any).currentDomain}/cloud/domains`} target="_blank" rel="noopener noreferrer">
                    Cloud Domains
                  </a>
                </ConfigModalNote>
              </>
            )}
          </ConfigModalBody>
        </Form>
      </DefaultModal>
    );
  }
}
